import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { apiAppFetch } from '../utils/apiFetch';
import { isWeekSettlement, weekRangeFromOrder } from '../utils/weekBillHelpers';
import '../AccountPages.css';

function Ledger() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await apiAppFetch('ledger', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) return;

      const dataa = await response.json();
      setData(Array.isArray(dataa) ? dataa : []);
    } catch (error) {
      // fetch failed silently
    }
  };

  const closingBalance = data.length > 0 ? Number(data[data.length - 1].mainAmount) : 0;

  const grouped = useMemo(() => {
    const newestFirst = [...data].reverse();
    const map = [];
    newestFirst.forEach((order) => {
      const label = order.dateTime
        ? new Date(order.dateTime).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : 'Unknown date';
      const last = map[map.length - 1];
      if (!last || last.label !== label) {
        map.push({ label, items: [order] });
      } else {
        last.items.push(order);
      }
    });
    return map;
  }, [data]);

  const openWeekBill = (order) => {
    const range = weekRangeFromOrder(order);
    const params = new URLSearchParams({
      s: range.start,
      e: range.end,
    });
    const path = `/week-bill?${params.toString()}`;
    if (window.ReactNativeWebView) {
      navigate(path);
      return;
    }
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const formatAmount = (value) =>
    Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={`account-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="ledger-hero">
        <div className="ledger-hero-label">Closing Balance</div>
        <div className="ledger-hero-value">₹{formatAmount(closingBalance)}</div>
      </div>

      {grouped.length === 0 ? (
        <div className="account-empty">
          <h3>No ledger entries</h3>
          <p>Transactions will appear here.</p>
        </div>
      ) : (
        grouped.map((group) => (
          <section key={group.label}>
            <div className="ledger-date-label">{group.label}</div>
            <div className="account-panel">
              {group.items.map((order) => {
                const weekSettlement = isWeekSettlement(order);
                const isCredit = String(order.transactionType) === '1';
                const amount = Number(order.transactionAmount);
                return (
                  <div
                    key={order.transactionId}
                    className={`ledger-tx${weekSettlement ? ' is-link' : ''}`}
                    onClick={weekSettlement ? () => openWeekBill(order) : undefined}
                    role={weekSettlement ? 'button' : undefined}
                    tabIndex={weekSettlement ? 0 : undefined}
                    onKeyDown={weekSettlement ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openWeekBill(order);
                      }
                    } : undefined}
                  >
                    <div>
                      <div className="ledger-tx-remark">
                        {order.manualRemark?.trim() || order.autoRemark}
                        {weekSettlement ? ' · Open bill' : ''}
                      </div>
                      <div className="ledger-tx-sub">Bal ₹{formatAmount(order.mainAmount)}</div>
                    </div>
                    <div className={`ledger-tx-amt ${isCredit ? 'credit' : 'debit'}`}>
                      {isCredit ? '+' : '-'}{formatAmount(amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

export default Ledger;
